/*
 *+------------------------------------------------------------------------+
 *| Licensed Materials - Property of IBM
 *| IBM Cognos Products: Dashboard
 *| (C) Copyright IBM Corp. 2018, 2022
 *|
 *| US Government Users Restricted Rights - Use, duplication or disclosure
 *| restricted by GSA ADP Schedule Contract with IBM Corp.
 *+------------------------------------------------------------------------+
 */
define([
	'../../lib/@waca/core-client/js/core-client/ui/core/Class',
	'./SmartsVisRecommenderService',
	'./SmartsToVizMapping',
	'./VisToSmartsMapping',
	'./IRUtils',
	'./SmartsSupportedChartTypes',
	'underscore',
	'./SmartsCompareCardRecommenderHelper',
	'../../apiHelpers/SlotAPIHelper'
], function (
	Class,
	SmartsVisRecommenderService,
	SmartsToVizMapping,
	VisToSmartsMapping,
	IRUtils,
	SmartsSupportedChartTypes,
	_,
	SmartsCompareCardRecommenderHelper,
	SlotAPIHelper
) {
	'use strict';

	const UnsupportedSmartsVis = [
		'com.ibm.vis.rave2network'
	];

	let BaseSmartsVisRecommenderWrapper = Class.extend({

		// constructor
		init: function ({
			moserDataSources,
			ajaxSvc,
			logger,
			glassOptions,
			recommenderInternal,
			dataSources
		}) {
			BaseSmartsVisRecommenderWrapper.inherited('init', this, arguments);

			this.moserDataSources = moserDataSources;

			this._recommenderService = new SmartsVisRecommenderService({
				ajaxSvc: ajaxSvc,
				logger: logger
			});
			this.dataSources = dataSources;
			this._glassOptions = glassOptions;
			this._smartsToVisInfoMap = SmartsToVizMapping;
			this._visToSmartsInfoMap = VisToSmartsMapping;
			this._supportedVisIds = Object.keys( this._smartsToVisInfoMap );
			this._recommenderApi = recommenderInternal;
		},

		destroy: function () {
			BaseSmartsVisRecommenderWrapper.inherited('destroy', this, arguments);

			if (this._recommenderService) {
				this._recommenderService.destroy();
				this._recommenderService = null;
			}

			this.dataSources = null;
			this._glassOptions = null;
			this._smartsToVisInfoMap = null;
			this._visToSmartsInfoMap = null;
			this._supportedVisIds = null;
			this._recommenderApi = null;
		},

		/**
		* Returns alternate visualizations recommendation for the given columnIds
		* @params - columnIds
		* @params - requestOptions
		*				{
		*					assetId, (mandatory) storeId of embeddedModule, uploadedFile or module)
		*					sourceType, (mandatory) 'uploadedFile', 'dataSet2' or 'module'
		*					oneChartPerType, (optional) true or false, default is true - returns one recommendation per chart type.
		*					numResult, (optional) number of recommendations, default is 10
		*				}
		* @returns - [recommendations] where recommendation is
		*	{
		*		visId: <visId>
		*		slots : {slotId_1 : [columnIds],
		*			    slotId_2: [columnIds],
		*				:
		*				}
		*		label: <label>
		*		description: <description>
		*		title: <title>
		*	}
		*/
		recommendAlternateVisualizations: function(columnIds=[], requestOptions = {}){
			return this._getRequestParametersForRecommendVisualizations(columnIds, requestOptions, true /*partialTempModule*/)
				.then(this._recommenderService.recommendAlternateVisualizationsOrBindings.bind(this._recommenderService))
				.then(this._transformRecommendationResponse.bind(this, requestOptions.module.getSourceId()))
				.then(this._stripDuplicates.bind(this));
		},

		/**
		* Returns a list of recommended related visualizations based on given columnIds.
		* @params - columnIds
		* @params - requestOptions
		*				{
		*					assetId, (mandatory) storeId of embeddedModule, uploadedFile or module)
		*					sourceType, (mandatory) 'uploadedFile', 'dataSet2' or 'module'
		*					oneChartPerType, (optional) true or false, default is true - returns one recommendation per chart type.
		*					numResult, (optional) number of recommendations, default is 10
		*				}
		* @returns - [recommendations] where recommendation is
		*	{
		*		visId: <visId>
		*		slots : {slotId_1 : [columnIds],
		*			    slotId_2: [columnIds],
		*				:
		*				}
		*		label: <label>
		*		description: <description>
		*		title: <title>
		*	}
		*/
		recommendRelatedVisualizations: function(columnIds=[], requestOptions = {}){
			return this._getRequestParametersForRecommendVisualizations(columnIds, requestOptions)
				.then( this._recommenderService.recommendRelatedVisualizations.bind(this._recommenderService))
				.then( this._transformRecommendationResponse.bind(this, requestOptions.module.getSourceId()));
		},


		recommendCompareVisualizations: function(columnIds=[], requestOptions){
			const helper = new SmartsCompareCardRecommenderHelper(columnIds, requestOptions);
			return helper.getRequestParameters().then((params) => {
				return this._recommenderService.recommendCompareVisualizations(params)
					.then( helper.transformCompareRecommendationResponse.bind(helper, requestOptions.module, requestOptions.widgetSpec));
			});
		},

		_getIncludedCharts() {
			return SmartsSupportedChartTypes.get(this._glassOptions, this._recommenderApi.getChartTypeList());
		},

		_getRequestParametersForRecommendVisualizations: function( columnIds, requestOptions, bPartialTempModule){

			const { visualization, moserDataSources } = requestOptions;
			if (visualization && moserDataSources && moserDataSources.isConsumerGroupColumn) {
				const sourceId = visualization.getDataSource().getId();
				columnIds.forEach((columnId, i) => {
					if (moserDataSources.isConsumerGroupColumn(sourceId, columnId)) {
						const customGroup = moserDataSources.getCustomGroupData(sourceId, columnId);
						if (customGroup && customGroup.basedOnMoserObjectId) {
							columnIds[i] = customGroup.basedOnMoserObjectId;
						}
					}
				});
			}

			let columns = this._getColumnsInfo(columnIds);

			let requestParameters = {
				columns: columns
			};
			if (requestOptions.includeBestVisForTargetFields) {
				requestParameters.includeBestVisForTargetFields = true;
			}

			return this._getIncludedCharts()
				.then( supporteChartTypes => {
					if( supporteChartTypes ){
						requestParameters.includedCharts = supporteChartTypes;
					}

					return this._addCommonRequestParameters(requestParameters, requestOptions, bPartialTempModule);
				});
		},

		/**
		* Given a visualization type, recommends the binding for the given columns
		* @param visId - visId
		* @param columnsToBind - array of columnIds, these are columns that need to be bound
		* @param boundColumns - array of bound column info --> [{slotId : <slotId>
		*											             columnIds: <[columnIds]> }
		*/
		recommendBindings: function({ visId, columnsToBind, requestOptions, boundColumnsInfo, originalVisId }){


			let columns = [];

			if(columnsToBind){
				columns = columns.concat(this._getColumnsInfo(columnsToBind));
			}

			if (boundColumnsInfo) {
				/**
				 * presence of originalVisId indicates that this is a change viz request, therefore,
				 * include the binding info for the original viz
				 */
				let mapInfo =  this._visToSmartsInfoMap[originalVisId] || this._visToSmartsInfoMap[visId];

				if( !mapInfo ) {
					return Promise.reject(new Error('Invalid originalVisId or visId'));
				}
				columns = columns.concat(this._getColumnsInfoForBoundColumns(boundColumnsInfo, mapInfo));
			}

			const requestParameters =  {
				columns: columns,
				chartType: this._visToSmartsInfoMap[visId].chartType,
				allowBindingsWithWarnings: true
			};

			if( originalVisId  && this._visToSmartsInfoMap[originalVisId] ){
				requestParameters.originalChartType = this._visToSmartsInfoMap[originalVisId].chartType;
			}

			return this._addCommonRequestParameters(requestParameters, requestOptions, true /*bPartialTempModule*/).then(() => {
				return this._recommenderService.recommendAlternateVisualizationsOrBindings( requestParameters )
					.then( this._transformBindingRecommendationResponse.bind(this, visId, requestOptions.module));
			});
		},

		/**
		 * Get recommendation info from the chartInfo endpoint of smarts server
		 * @param {object}	options
		 * @param {array} 	options.columns - list of columns and which slots they're mapped to
		 * @param {string}	options.visId - id of vis type
		 * @param {object}	options.requestOptions - other request-specific options
		 * @return {object} payload from smarts server (including `label` attribute)
		 */
		getRecommendInfo: function({ columns, visId, requestOptions }) {
			const mapInfo = this._visToSmartsInfoMap[visId];

			if(!mapInfo) {
				return Promise.reject(new Error(this.getUnsupportedVizErrCode()));
			}

			columns = this._sanitizeColumnInfo(columns);

			const requestParameters = {
				columns: this._getColumnsInfoForBoundColumns(columns, mapInfo),
				chartType: this._visToSmartsInfoMap[visId].chartType
			};

			return this._addCommonRequestParameters(requestParameters, requestOptions, true /*bPartialTempModule*/).then(() => {
				return this._recommenderService.getChartInfo( requestParameters );
			});
		},

		getSmartsToVizMappingsForVisId(visId) {
			return this._visToSmartsInfoMap[visId];
		},

		isVisSlotKnownToSmarts(visId, slotIdOfInterest) {
			let smartsToVizMappings = this.getSmartsToVizMappingsForVisId(visId);
			const mappingKeys = smartsToVizMappings && _.keys(smartsToVizMappings);
			return mappingKeys ? (_.filter(mappingKeys, key => {return key === slotIdOfInterest.getId();}).length > 0) : true; //Defualt to true
		},

		getUnsupportedVizErrCode: function(){
			return 'ERR_CODE_UNSUPPORTED_VIS';
		},

		_isSupportedViz: function(recommendation){
			return this._supportedVisIds.indexOf( recommendation.chartType ) !== -1;
		},

		_addCommonRequestParameters: function(requestParameters, { assetId, numResults, sourceType, oneChartPerType, module, filters }, bPartialTempModule){
			const columnIds = requestParameters.columns
				.map( column => column.id)
				.reduce((acc, columnIds) => acc.concat(columnIds), []);

			let tempModuleJSON = ( bPartialTempModule
				? module.copyPartialMoserModuleJSON(columnIds)
				: module.getTemporarySessionModuleJSON() );

			let commonRequestParameters = {
				sourceModuleId: assetId,
				oneChartPerType: oneChartPerType || true,
				numResults: numResults || 10, //upper limit of recommendatations returned. 10 is arbitrary.
				sourceType: sourceType,
				shapedModule: (!!module.getShapingId()) //module has shaping
			};

			if( tempModuleJSON ){
				commonRequestParameters.tempModule = JSON.stringify( tempModuleJSON );
			}

			if (filters) {
				commonRequestParameters.filters = filters;
			}
			return module.saveTemporaryModule().then((shapingModuleId) => {
				if (assetId !== shapingModuleId) {
					commonRequestParameters.tempModuleId = shapingModuleId;
				}
				return _.extend(requestParameters, commonRequestParameters);
			});
		},

		_transformRecommendationResponse: function(dataSourceId, recommendations){
			return recommendations
				.filter( this._isSupportedViz.bind(this))
				.filter( this._isSupportedBinding.bind(this, dataSourceId))
				.filter( this._hasSupportedFilter.bind(this))
				.map(this._transformRecommendation.bind(this));
		},

		//remove the duplicate recommendations but keep the first occurrence and preserve the order
		_stripDuplicates: function(recommendations){
			const result = [];
			const map = new Map();
			for (const recommendation of recommendations) {
				if(!map.has(recommendation.visId)){
					map.set(recommendation.visId, true);
					result.push(recommendation);
				}
			}
			return result;
		},

		/**
		* This method should be overriden as needed to provide the proper transformation
		* @ params {recommendation}
		* @ returns
		*	{
		*		visId: <visId>
		*		slots : {slotId_1 : [columnIds],
		*			    slotId_2: [columnIds],
		*				:
		*				}
		*		label: <label>
		*		description: <description>
		*		title: <title>
		*	}
		*/
		_transformRecommendation: function(recommendation){
			const { chartType, label, description, title, columns, filters, autoGroupingMap } = recommendation;
			const smartsToVisMap = this._smartsToVisInfoMap[chartType];

			return {
				label: label,
				description: description,
				title: title,
				visId: smartsToVisMap.visId,
				topBottom: this._transformTopBottomFromSmartsFilters(filters),
				filters: this._transformFiltersFromSmartsFilters(filters),
				autoGroup: this._transformAutoGroupFromSmartsAutoGroupingMap(autoGroupingMap),
				slots: this._transformFromSmartsColumnInfo(columns, smartsToVisMap)
			};
		},

		_getColumnsInfo: function( columnIds ){
			return columnIds.map(columnId => ({ id: [columnId] }));
		},

		_sanitizeColumnInfo: function(boundColumnsInfo){
			return boundColumnsInfo.map( info => ({
				slotId: info.slotId,
				columnIds: _.without(info.columnIds, SlotAPIHelper.MULTI_MEASURES_SERIES)
			})).filter( info => info.columnIds.length > 0);
		},

		/** Massage the given boundColumnsInfo into one that Smarts understand
		*	Transform
		*	[{
		*		slotId: <slotId>,
		*		columnIds: [<column ids>]
		*   }, {..}]
		* 	to
		*	[{
		*		slot: <slot name>
		*		id: [<column ids>]
		*	}, {..}]
		*
		*/
		_getColumnsInfoForBoundColumns: function(boundColumnsInfo, mapInfo){
			return boundColumnsInfo.map( columnInfo => ({
				slot: mapInfo[columnInfo.slotId],
				id: columnInfo.columnIds
			}));
		},

		_transformAutoGroupFromSmartsAutoGroupingMap: function(autoGroupingMap = {}) {
			return Object.keys(autoGroupingMap)
				.map(itemId => {
					const value = autoGroupingMap[itemId];
					return {
						itemId,
						...value
					};
				});
		},

		_transformTopBottomFromSmartsFilters: function(filters = []) {
			return filters
				.filter(filter => filter.type === 'TOP_BOTTOM')
				.map(filter => {
					return {
						itemId: filter.columnId,
						spec: {
							type: IRUtils.IRToTopBottomType(filter.topBottomType, filter.method),
							value: filter.domainSize,
							context: filter.byColumn,
							aggregate: 'sum' // TODO
						}
					};
				});
		},

		_transformFiltersFromSmartsFilters: function(filters = []) {
			return filters
				.filter(filter => filter.type !== 'TOP_BOTTOM')
				.map(filter => {
					return IRUtils.IRFilters2LocalFilter(filter);
				});
		},

		_transformFromSmartsColumnInfo: function(columns, smartsToVisMap){
			let slotToColumnIdsMap = {};

			columns.forEach( column => {
				const slotId = smartsToVisMap[column.slot];
				let columnIds = slotToColumnIdsMap[slotId] || [];

				slotToColumnIdsMap[slotId] = columnIds.concat(column.id);
			});
			return slotToColumnIdsMap;
		},

		/*
		* When a visualization is not supported by Smarts vis recommender,  it is map to one that is supported
		* that has the same number and type of slots.  This is only for recommend binding request.
		 */
		_getSmartsToVisMappingForUnsupportedVisualization: function(visId){
			let smartsToVisMap = {};
			const oldMapInfo = this._visToSmartsInfoMap[visId];
			const chartProps = Object.keys(oldMapInfo);
			chartProps.forEach(function (prop) {
				if (prop === 'chartType') {
					smartsToVisMap.visId = visId;
				} else {
					smartsToVisMap[oldMapInfo[prop]] = prop;
				}
			});
			return smartsToVisMap;
		},

		_transformBindingRecommendationResponse: function(visId, module, bindingRecommendations){
			let smartsToVisMap;
			if( UnsupportedSmartsVis.indexOf(visId) > -1 ){
				smartsToVisMap = this._getSmartsToVisMappingForUnsupportedVisualization(visId);
			}
			return bindingRecommendations
				.map( bindingRec => {
					const { chartType, columns, unbound } = bindingRec;
					smartsToVisMap = !smartsToVisMap ? this._smartsToVisInfoMap[chartType] : smartsToVisMap;

					return {
						visId: visId || smartsToVisMap.visId,
						slots: this._transformFromSmartsColumnInfo(columns, smartsToVisMap),
						unbound: unbound
					};
				});
		},

		/**
		 * 	Returns true if the slot binding recommendation is supported for the
		 * 	specified chart type
		 */
		_isSupportedBinding: function(dataSourceId, { chartType, columns } ){
			switch( chartType.toUpperCase()) {

				case 'SPIRAL':
					return this._isSupportedSpiral(dataSourceId, columns);

				default:
					return true;
			}
		},

		_hasSupportedFilter: function( recommendation ) {
			if( recommendation ) {
				const filters = recommendation.filters;
				if(filters && filters.length > 0 ) {
					const isProjected = (columnId) => {
						const columns = recommendation.columns || [];
						const isOnASlot = columns.some( (column) => {
							if( column.id.includes( columnId )) {
								return true;
							}
							return false;
						} );
						return isOnASlot;
					};
					const isIncompatible = filters.some( (filter) => {
						if( filter.type === 'TOP_BOTTOM' ) {
							if( !isProjected( filter.columnId) ) {
								return true;
							} else {
								return false;
							}
						}
						return false;
					});
					return !isIncompatible;
				}
			}
			return true;
		},

		/**
		 * Returns false if Driver analysis viz has attribute type bound to TARGET slot
		 * @param module
		 * @param columns
		 * @returns {boolean}
		 * @private
		 */
		_isSupportedSpiral: function(dataSourceId, columns) {
			const dataSource = this.dataSources.getDataSource(dataSourceId);
			const containsOlapItem = columns.some((column) => {
				const mdColumn = dataSource.getMetadataColumn(column.id);
				return mdColumn && mdColumn.isOlapColumn();
			});

			const module = this.moserDataSources.getModule(dataSourceId);
			// Spiral/Comet/Sunburst/Decision Tree charts are not supported for OLAP data
			return !(containsOlapItem || module.isOlapPackage());
		}

	});
	return BaseSmartsVisRecommenderWrapper;
});
